import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ResponsiveContainer,
    Cell
} from "recharts";
import { theme } from "../theme";
import SkeletonCard from "./common/SkeletonCard";
import EmptyState from "./common/EmptyState";
import DashboardCard from "./common/DashboardCard";

import { API_BASE } from '../api/config';

const MONTH_LABELS = [
    "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
    "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
];

const ClosedLostActions = React.memo(({ onMaximize, fullScreen }) => (
    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
        {onMaximize && !fullScreen && (
            <button
                onClick={onMaximize}
                style={{
                    padding: "8px",
                    background: "rgba(255, 255, 255, 0.05)",
                    color: "#e5e7eb",
                    borderRadius: 8,
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center"
                }}
                title="Maximize"
            >
                <span style={{ fontSize: "16px", lineHeight: 1 }}>⤢</span>
            </button>
        )}
        {fullScreen && onMaximize && (
            <button
                onClick={onMaximize}
                style={{
                    padding: "8px",
                    background: "rgba(244, 67, 54, 0.2)",
                    color: "#f44336",
                    border: "1px solid #f44336",
                    borderRadius: "50%",
                    width: "36px",
                    height: "36px",
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center"
                }}
            >

            </button>
        )}
    </div>
));

const ClosedLostSubtitle = React.memo(({ salesperson, year, region, fullScreen }) => (
    <p style={{
        color: "#f8fafc",
        fontSize: fullScreen ? "24px" : "18px",
        marginTop: fullScreen ? "0px" : "-10px",
        marginBottom: theme.spacing.lg,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        fontWeight: 700,
        letterSpacing: "0.5px",
        textAlign: fullScreen ? "center" : "left",
        textShadow: "0 2px 4px rgba(0,0,0,0.5)"
    }}>
        {salesperson && <span style={{ color: "#ff8a3d" }}>{salesperson}</span>}
        {year && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {year}</span>}
        {region && <span style={{ color: "#cbd5e1", fontWeight: 500 }}> · {region === "all" ? "All Regions" : region}</span>}
    </p>
));

const ClosedLostSummary = React.memo(({ totalClosedLost, year, fullScreen, displayCurrency = "AED" }) => (
    <div
        style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            marginTop: "18px",
            marginBottom: "18px",
            position: "relative",
            zIndex: 1,
        }}
    >
        <div
            style={{
                flex: "1 1 auto",
                minWidth: "220px",
                background: "linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(239,68,68,0.02) 100%)",
                borderRadius: 12,
                padding: fullScreen ? "18px 24px" : "14px 20px",
                border: "1px solid rgba(239,68,68,0.2)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
        >
            <div
                style={{
                    color: "#f87171",
                    fontSize: 11,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    marginBottom: 6,
                }}
            >
                Total Closed Lost ({year})
            </div>
            <div
                style={{
                    color: "#ffffff",
                    fontSize: fullScreen ? 28 : 24,
                    fontWeight: 800,
                }}
            >
                {displayCurrency} {Math.round(totalClosedLost).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
        </div>

        <div
            style={{
                flex: "1 1 auto",
                minWidth: "220px",
                background: "linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(99, 102, 241, 0.02) 100%)",
                borderRadius: 12,
                padding: fullScreen ? "18px 24px" : "14px 20px",
                border: "1px solid rgba(99, 102, 241, 0.2)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            }}
        >
            <div
                style={{
                    color: "#818cf8",
                    fontSize: 11,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    fontWeight: 600,
                    marginBottom: 6,
                }}
            >
                Avg Closed Lost / Month
            </div>
            <div
                style={{
                    color: "#ffffff",
                    fontSize: fullScreen ? 28 : 24,
                    fontWeight: 800,
                }}
            >
                {displayCurrency}{" "}
                {Math.round(totalClosedLost / 12)
                    .toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
        </div>
    </div>
));

function ClosedLostPredictor({ filterContext, onMaximize, fullScreen = false }) {
    const { salesperson, year, region, displayCurrency = "AED" } = filterContext || {};
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError("");
            const res = await axios.get(
                `${API_BASE}/api/metrics/closed-lost-monthly`,
                {
                    params: {
                        salesperson,
                        year,
                        region: region || "all",
                    },
                }
            );

            const rows = res.data.data || [];

            const monthMap = {};
            rows.forEach((r) => {
                const m = r.month || 1;
                monthMap[m] = Number(r.value_aed || 0);
            });

            const mapped = MONTH_LABELS.map((name, idx) => ({
                month: name,
                monthIndex: idx + 1,
                value_aed: monthMap[idx + 1] || 0,
            }));

            setData(mapped);
        } catch (err) {
            console.error("ClosedLostPredictor error", err);
            setError(err.message || "Failed to load closed lost monthly data");
        } finally {
            setLoading(false);
        }
    }, [salesperson, year, region]);

    useEffect(() => {
        if (!salesperson || !year) return;
        fetchData();
    }, [salesperson, year, fetchData]);

    const hasData = useMemo(() => data.some((d) => d.value_aed > 0), [data]);
    const totalClosedLost = useMemo(() => data.reduce((sum, d) => sum + d.value_aed, 0), [data]);

    const actionButtons = useMemo(() => (
        <ClosedLostActions onMaximize={onMaximize} fullScreen={fullScreen} />
    ), [onMaximize, fullScreen]);

    const ChartArea = () => (
        <div style={{
            flex: fullScreen ? "1 1 auto" : "none",
            width: "100%",
            height: fullScreen ? "auto" : "350px",
            minHeight: 0,
            marginTop: fullScreen ? "20px" : "20px"
        }}>
            <ResponsiveContainer width="100%" height={fullScreen ? "90%" : 350}>
                <BarChart
                    data={data}
                    margin={{
                        top: fullScreen ? 20 : 10,
                        right: fullScreen ? 50 : 30,
                        left: fullScreen ? 20 : 0,
                        bottom: fullScreen ? 80 : 20,
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis
                        dataKey="month"
                        stroke="#f87171"
                        tick={{
                            fill: "#ffffff",
                            fontSize: fullScreen ? 14 : 11,
                            fontWeight: 700,
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
                        }}
                        angle={-30}
                        textAnchor="end"
                        height={fullScreen ? 100 : 75}
                        interval={0}
                        tickFormatter={(v) => v.substring(0, 3)}
                        axisLine={{ stroke: "rgba(255,255,255,0.3)", strokeWidth: 2 }}
                        label={{
                            value: 'MONTHS',
                            position: 'insideBottom',
                            offset: -5,
                            fill: '#94a3b8',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 1
                        }}
                    />
                    <YAxis
                        stroke="#94a3b8"
                        tick={{
                            fill: "#ffffff",
                            fontSize: fullScreen ? 14 : 11,
                            fontWeight: 700,
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
                        }}
                        tickFormatter={(v) =>
                            `${(v / 1_000_000).toFixed(1)}M`
                        }
                        width={fullScreen ? 70 : 60}
                        axisLine={{ stroke: "rgba(255,255,255,0.3)", strokeWidth: 2 }}
                        label={{
                            value: `VALUES (${displayCurrency})`,
                            angle: -90,
                            position: 'insideLeft',
                            offset: 10,
                            fill: '#94a3b8',
                            fontSize: 12,
                            fontWeight: 700,
                            letterSpacing: 1,
                            style: { textAnchor: 'middle' }
                        }}
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: "rgba(15, 23, 42, 0.98)",
                            backdropFilter: "blur(12px)",
                            border: "1px solid rgba(255, 99, 71, 0.3)",
                            borderRadius: 12,
                            padding: fullScreen ? "16px 20px" : "12px 16px",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.6)"
                        }}
                        cursor={{ fill: 'rgba(248, 113, 113, 0.1)' }}
                        labelStyle={{
                            fontSize: fullScreen ? 15 : 13,
                            fontWeight: 800,
                            marginBottom: 8,
                            color: "#ffffff",
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
                        }}
                        itemStyle={{
                            fontSize: fullScreen ? 14 : 12,
                            color: "#fca5a5",
                            fontWeight: 600,
                            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"
                        }}
                        formatter={(value) => [
                            `${displayCurrency} ${Math.round(Number(value)).toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
                            "Closed Lost Value",
                        ]}
                    />
                    <defs>
                        <linearGradient id="closedLostGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#ef4444" stopOpacity={1} />
                            <stop offset="100%" stopColor="#b91c1c" stopOpacity={0.8} />
                        </linearGradient>
                    </defs>
                    <Bar
                        dataKey="value_aed"
                        name={`Closed Lost (${displayCurrency})`}
                        fill="url(#closedLostGradient)"
                        radius={[6, 6, 2, 2]}
                        animationDuration={1000}
                    >
                        {data.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                style={{ transition: 'all 0.3s ease' }}
                            />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );

    if (!salesperson || !year) return null;

    return (
        <DashboardCard
            title="Closed Lost Monthly"
            actions={actionButtons}
            fullScreen={fullScreen}
            gradient="danger"
        >
            <ClosedLostSubtitle salesperson={salesperson} year={year} region={region} fullScreen={fullScreen} />
            {loading && <SkeletonCard height="400px" />}

            {error && (
                <div style={{
                    padding: theme.spacing.lg,
                    background: "rgba(239, 68, 68, 0.1)",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.colors.danger}`,
                    color: "#fca5a5",
                }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            {!loading && !error && !hasData && (
                <EmptyState
                    icon=""
                    message="No Closed Lost data found for the selected filters."
                    size="medium"
                />
            )}

            {!loading && !error && hasData && (
                <div style={{
                    height: fullScreen ? "100%" : "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: fullScreen ? "center" : "flex-start",
                    overflowY: fullScreen ? "hidden" : "visible",
                    paddingBottom: fullScreen ? "20px" : "0"
                }}>
                    <ClosedLostSummary totalClosedLost={totalClosedLost} year={year} fullScreen={fullScreen} displayCurrency={displayCurrency} />
                    <ChartArea />
                </div>
            )}
        </DashboardCard>
    );
}

export default ClosedLostPredictor;
